import type { QueryClient } from "@tanstack/react-query";

export type TargetedConversationSummary = {
  id: string;
  targetMemberUserId?: string;
};

export const targetedConversationsQueryKey = (hubId: string) =>
  [`/api/messenger/${hubId}/targeted-conversations`] as const;

export const targetedMessagesQueryKey = (hubId: string, conversationId: string | null) =>
  [`/api/messenger/${hubId}/targeted-conversations/${conversationId}/messages`] as const;

export function isTargetedAccessRevocationError(error: unknown): boolean {
  return getTargetedAccessRevocationStatus(error) !== null;
}

export function getTargetedAccessRevocationStatus(error: unknown): number | null {
  if (!error) return null;

  if (typeof error === "object") {
    const candidate = error as { status?: unknown; statusCode?: unknown };
    for (const status of [candidate.status, candidate.statusCode]) {
      if (status === 401 || status === 403 || status === 404) return status;
      if (typeof status === "string" && ["401", "403", "404"].includes(status)) return Number(status);
    }
  }

  const message = error instanceof Error
    ? error.message
    : typeof error === "string"
      ? error
      : "";
  const match = message.match(/(?:^|[^\d])(401|403|404)(?=\D|$)/);
  return match ? Number(match[1]) : null;
}

export function canRenderTargetedMessages(options: {
  selectedConversationId: string | null;
  selectedConversationIsCurrent: boolean;
  conversationListError?: unknown;
  memberListError?: unknown;
  messagesError?: unknown;
}): boolean {
  return Boolean(
    options.selectedConversationId
    && options.selectedConversationIsCurrent
    && !isTargetedAccessRevocationError(options.conversationListError)
    && !isTargetedAccessRevocationError(options.memberListError)
    && !isTargetedAccessRevocationError(options.messagesError),
  );
}

export function clearTargetedConversationCache(
  queryClient: QueryClient,
  hubId: string,
  conversationId: string | null,
): void {
  if (!conversationId) return;

  queryClient.removeQueries({
    queryKey: targetedMessagesQueryKey(hubId, conversationId),
    exact: true,
  });
  queryClient.setQueryData<TargetedConversationSummary[]>(
    targetedConversationsQueryKey(hubId),
    (conversations) => conversations?.filter((conversation) => conversation.id !== conversationId),
  );
}

export function clearTargetedConversationListCache(queryClient: QueryClient, hubId: string): void {
  const queryKey = targetedConversationsQueryKey(hubId);
  const cachedConversations = queryClient.getQueryData<TargetedConversationSummary[]>(queryKey);
  if (!cachedConversations?.length) return;

  queryClient.setQueryData<TargetedConversationSummary[]>(
    queryKey,
    [],
  );
}

export function reconcileTargetedConversationSelection(
  queryClient: QueryClient,
  hubId: string,
  options: {
    selectedConversationId: string | null;
    conversations?: TargetedConversationSummary[];
    conversationListLoaded: boolean;
    conversationListError?: unknown;
    messagesError?: unknown;
    eligibleMemberIds?: ReadonlySet<string>;
    memberListLoaded?: boolean;
    memberListError?: unknown;
  },
): string | null {
  const selectedId = options.selectedConversationId;
  const listAccessWasRevoked = isTargetedAccessRevocationError(options.conversationListError);
  const memberAccessWasRevoked = isTargetedAccessRevocationError(options.memberListError);
  if (!selectedId) {
    if (listAccessWasRevoked || memberAccessWasRevoked) {
      clearTargetedConversationListCache(queryClient, hubId);
    }
    return null;
  }

  const selectedConversation = options.conversations?.find(
    (conversation) => conversation.id === selectedId,
  );
  const memberIsNoLongerEligible = Boolean(
    options.memberListLoaded
    && selectedConversation?.targetMemberUserId
    && !options.eligibleMemberIds?.has(selectedConversation.targetMemberUserId),
  );
  const accessWasRevoked = listAccessWasRevoked
    || memberAccessWasRevoked
    || isTargetedAccessRevocationError(options.messagesError);
  const conversationDisappeared = options.conversationListLoaded && !selectedConversation;

  if (!accessWasRevoked && !conversationDisappeared && !memberIsNoLongerEligible) {
    return selectedId;
  }

  clearTargetedConversationCache(queryClient, hubId, selectedId);
  if (
    listAccessWasRevoked
    || memberAccessWasRevoked
  ) {
    clearTargetedConversationListCache(queryClient, hubId);
  }
  return null;
}